import { useEffect, useRef, useState } from "react";
import { ServerCog, Plus, RefreshCw, Trash2, Loader2, ShieldCheck, Sparkles, ShieldOff, Code2 } from "lucide-react";
import axios, { API } from "@/lib/apiClient";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const PULSE_INTERVAL_MS = 15000;
const AT_RISK_STATES = ["re_verifying", "quarantined"];

const pulseColor = (score) => {
  if (score === null || score === undefined) return "#64748B";
  if (score >= 80) return "#22C55E";
  if (score >= 40) return "#F59E0B";
  return "#F97316";
};

export const NodeIntegrityPanel = ({ auditId, readOnly = false, isRootAdmin = false }) => {
  const [nodes, setNodes] = useState([]);
  const [integrityScore, setIntegrityScore] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [checkingId, setCheckingId] = useState(null);
  const pollRef = useRef(null);

  const fetchNodes = async () => {
    try {
      const { data } = await axios.get(`${API}/audits/${auditId}/nodes`);
      setNodes(data.nodes || []);
      setIntegrityScore(data.integrity_score);
    } catch {
      // silent - heartbeat poll, don't spam toasts
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    fetchNodes();
    pollRef.current = setInterval(fetchNodes, PULSE_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [auditId]);

  const addNode = async () => {
    if (!label.trim() || !url.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/audits/${auditId}/nodes`, { label: label.trim(), url: url.trim() });
      toast.success("Node added - running first Pulse Check now.");
      setLabel("");
      setUrl("");
      setAddOpen(false);
      fetchNodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add that node.");
    } finally {
      setBusy(false);
    }
  };

  const checkNow = async (nodeId) => {
    setCheckingId(nodeId);
    try {
      await axios.post(`${API}/audits/${auditId}/nodes/${nodeId}/check`);
      fetchNodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Check failed.");
    } finally {
      setCheckingId(null);
    }
  };

  const deleteNode = async (nodeId) => {
    setCheckingId(nodeId);
    try {
      await axios.delete(`${API}/audits/${auditId}/nodes/${nodeId}`);
      setNodes((prev) => prev.filter((n) => n.id !== nodeId));
    } catch {
      toast.error("Could not remove node.");
    } finally {
      setCheckingId(null);
    }
  };

  const clearQuarantine = async (nodeId) => {
    setCheckingId(nodeId);
    try {
      await axios.post(`${API}/audits/${auditId}/nodes/${nodeId}/clear-quarantine`);
      toast.success("Quarantine cleared.");
      fetchNodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not clear quarantine.");
    } finally {
      setCheckingId(null);
    }
  };

  const copyEmbedCode = async (nodeId) => {
    const snippet = `<iframe src="${process.env.REACT_APP_BACKEND_URL}/api/badge/${nodeId}/widget" width="240" height="40" frameborder="0" style="border:none;"></iframe>`;
    try {
      await navigator.clipboard.writeText(snippet);
      toast.success("Trust widget embed code copied - paste it on your own site.");
    } catch {
      toast.error("Could not copy. Copy manually: " + snippet);
    }
  };

  if (!loaded) return null;

  return (
    <div className="rounded-lg border border-surface-border bg-surface-card p-6" data-testid="node-integrity-panel">
      <div className="mb-1 flex items-center gap-2">
        <ServerCog className="h-4 w-4 text-openai-cyan" />
        <h3 className="font-heading text-lg font-bold text-white">Infrastructure Integrity Pulse</h3>
      </div>
      <p className="mb-4 text-sm text-slate-400">
        Distinct from your Visibility Score above &mdash; this tracks whether your own machines/websites are reachable, secure, and un-defaced.
      </p>

      <div className="mb-5 flex items-center gap-3" data-testid="node-integrity-score-row">
        <div
          className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full border-2 font-heading text-lg font-bold text-white"
          style={{ borderColor: pulseColor(integrityScore) }}
          data-testid="node-integrity-score-value"
        >
          {integrityScore ?? "-"}
        </div>
        <div>
          <p className="text-sm font-semibold text-white">{nodes.length === 0 ? "No nodes being watched yet" : `${nodes.length} node${nodes.length > 1 ? "s" : ""} under Sovereign DNA watch`}</p>
          <p className="text-xs text-slate-500">Checked independently, on its own loop &mdash; never blocks your brand audits.</p>
        </div>
      </div>

      {nodes.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="node-integrity-list">
          {nodes.map((n) => {
            const atRisk = AT_RISK_STATES.includes(n.badge_state);
            return (
              <div key={n.id} className="rounded-md border border-surface-border bg-obsidian p-3" data-testid={`node-row-${n.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-white">{n.label}</p>
                    <p className="truncate text-xs text-slate-500">{n.url}</p>
                    <span
                      className="mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                      style={{ color: n.display_color, backgroundColor: `${n.display_color}1A` }}
                      data-testid={`node-badge-state-${n.id}`}
                    >
                      {n.display_label}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="mr-2 hidden text-xs text-slate-500 sm:inline" data-testid={`node-score-${n.id}`}>
                      {n.integrity_score ?? "-"}/100
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyEmbedCode(n.id)}
                      className="gap-1 border-surface-border text-slate-200 hover:text-white"
                      data-testid={`node-copy-embed-${n.id}`}
                      title="Copy Public Trust Widget embed code"
                    >
                      <Code2 className="h-3.5 w-3.5" />
                    </Button>
                    {!readOnly && !atRisk && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={checkingId === n.id}
                        onClick={() => checkNow(n.id)}
                        className="gap-1 border-surface-border text-slate-200 hover:text-white"
                        data-testid={`node-check-now-${n.id}`}
                      >
                        {checkingId === n.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                      </Button>
                    )}
                    {!readOnly && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={checkingId === n.id}
                        onClick={() => deleteNode(n.id)}
                        className="border-sovereign/40 text-sovereign hover:bg-sovereign/10"
                        data-testid={`node-delete-${n.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
                {!readOnly && atRisk && (
                  <Button
                    size="sm"
                    disabled={checkingId === n.id}
                    onClick={() => checkNow(n.id)}
                    className="mt-3 w-full gap-2 bg-warning-amber text-obsidian hover:bg-warning-amber/90"
                    data-testid={`node-cure-btn-${n.id}`}
                  >
                    {checkingId === n.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    Cure This Node - Re-run Pulse Check Now
                  </Button>
                )}
                {isRootAdmin && n.status === "quarantined" && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={checkingId === n.id}
                    onClick={() => clearQuarantine(n.id)}
                    className="mt-2 w-full gap-2 border-truth-green/50 text-truth-green hover:bg-truth-green/10"
                    data-testid={`node-clear-quarantine-${n.id}`}
                  >
                    {checkingId === n.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldOff className="h-4 w-4" />}
                    Clear Quarantine (Root Admin)
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {readOnly ? (
        nodes.length === 0 && <p className="text-sm text-slate-500" data-testid="node-integrity-readonly-empty">Ask the account owner to add nodes to watch.</p>
      ) : addOpen ? (
        <div className="flex flex-col gap-2 sm:flex-row" data-testid="node-add-form">
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Label (e.g. Main Site)"
            className="flex-1 rounded-md border border-surface-border bg-obsidian px-3 py-2 text-sm text-white outline-none focus:border-openai-cyan"
            data-testid="node-add-label-input"
          />
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://yourbrand.com"
            className="flex-1 rounded-md border border-surface-border bg-obsidian px-3 py-2 text-sm text-white outline-none focus:border-openai-cyan"
            data-testid="node-add-url-input"
          />
          <Button onClick={addNode} disabled={busy} className="gap-2 bg-openai-cyan text-obsidian hover:bg-openai-cyan/90" data-testid="node-add-submit-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            Watch This Node
          </Button>
        </div>
      ) : (
        <Button
          variant="outline"
          onClick={() => setAddOpen(true)}
          disabled={nodes.length >= 5}
          className="gap-2 border-surface-border text-slate-200 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
          data-testid="node-add-open-btn"
        >
          <Plus className="h-4 w-4" /> {nodes.length >= 5 ? "Max 5 Nodes Reached" : "Add Node to Watch"}
        </Button>
      )}
    </div>
  );
};
